import React from 'react';

import { Check as CheckIcon } from '@styled-icons/boxicons-regular/Check';
import { formatDate } from 'shared/date';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Text,
  useMediaQuery,
  Button,
  Icon,
} from '@chakra-ui/react';

import AppTable from 'components/AppTable';
import { ModalRootProps } from 'components/Modal/Root';

import { Version } from 'types/base';

import { getStatusBadge } from './getStatusBadge';

interface ModalBaseVersionSelect extends ModalRootProps {
  data: {
    base_id: string;
    description: string;
    versions: Version[];
  };
}

const modalMargin = 120;
const modalMobileFix = 100;

const ModalBaseVersionSelect: React.FC<ModalBaseVersionSelect> = ({
  handleClose,
  onConfirm,
  data,
  ...restProps
}) => {
  const [isMobile] = useMediaQuery('(max-width: 728px)');

  const handleConfirm = (item: Version): void => {
    if (onConfirm) onConfirm(item);
    handleClose();
  };

  return (
    <Modal {...restProps} scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent
        sx={{
          width: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
          maxWidth: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
          height: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
          maxHeight: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
        }}
      >
        <ModalHeader>
          Referências da base {data.description ? `- ${data.description}` : ''}
        </ModalHeader>

        <ModalCloseButton />

        <ModalBody>
          <AppTable
            cols={[
              { field: 'created_at', description: 'Criação' },
              {
                field: 'description',
                description: 'Descrição',
                sortable: false,
              },
              { field: 'updated_at', description: 'Atualizado em' },
              { field: 'status', description: 'Status' },
              {
                field: 'total_compositions',
                description: 'Qtde. Composições',
                isCentered: true,
                sortable: false,
              },
              {
                field: 'total_inputs',
                description: 'Qtde. Insumos',
                isCentered: true,
                sortable: false,
              },
              { field: 'actions', description: 'Ações' },
            ]}
            mapping={(item) => ({
              id: item.id,
              created_at: (
                <>
                  <Text mb={1}>{formatDate(item.created_at)}</Text>
                </>
              ),
              description: (
                <>
                  <Text
                    mb={1}
                    maxWidth={{ base: '100%', md: '150px', lg: '200px' }}
                    whiteSpace="pre-wrap"
                    fontWeight="bold"
                  >
                    {item.description}
                  </Text>

                  <Text fontSize="smaller">
                    {item.year && item.month
                      ? `${String(item.month).padStart(2, '0')}/${item.year}`
                      : null}
                  </Text>
                </>
              ),
              updated_at: <Text>{formatDate(item.updated_at)}</Text>,
              status: getStatusBadge(item.status),
              total_compositions: String(item.total_compositions),
              total_inputs: String(item.total_inputs),
              actions: (
                <>
                  {item.status === 'editing' && (
                    <Button
                      leftIcon={<Icon as={CheckIcon} />}
                      colorScheme="green"
                      size="sm"
                      onClick={() => handleConfirm(item)}
                    >
                      Consolidar alterações
                    </Button>
                  )}
                </>
              ),
            })}
            data={data.versions}
            loading={false}
            onUpdateSort={() => {
              //
            }}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ModalBaseVersionSelect;
